import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { IconSeeker } from '@ui-seeker/core'

import { AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppEmptyLine, AppNote, AskIrisButton, secondaryButtonSx } from '@components'
import { besidePanel } from '@components/AskIris/besidePanel'
import { pushRight } from '@app/@signals'
import { userData } from '@signals/user'
import { isIrisUser } from 'Layout/PmsAgentWidget'
import { dsColorNew } from '@palette'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { acrissClassName } from 'Pages/MyBusiness/_shared/acrissClass'
import { headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { inUnit, money } from '../@services/amounts'
import { activityHref, queryOf } from '../@services/viewState'
import { deltaSentence, pickupLabel, unitWord, windowText } from '../@services/windowLabel'
import { acrissEvidence, curveEvidence } from '../@services/askEvidence'
import { buildPassCurve, completePasses, passCurveLabel, passTooltip, truncatedNote } from '../@services/passCurve'
import { fetchAcrissPushes } from '../@services/useLastChanges'
import { CurveChart, Tooltip as CurveTooltip, tipText } from 'Pages/BusinessLogistics/Dashboards/@components/PriceCurve'
import { Change, DeltaPill } from './Headline'
import { PointsCell, RatioCell } from './PushRows'


const tile = { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', p: 1.25, minWidth: 0 }
const tileTitle = { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }
const STATUS = { down: 'down', up: 'up', unchanged: 'no net change', no_decision: 'no decision', incomparable: 'not comparable' }

const CURVE_HEIGHT = 210

/**
 * The evolution curve of the ACRISS (phase 2, criterion 19): your average price and your tier's at each pass of the
 * window — `PriceCurve`'s `CurveChart` with `xKind="pass"`, ← → between passes, the hover read aloud. Data =
 * `/last-changes/pushes?sipp=` (that ACRISS over every date of the sample, up to 10 pages). A read cut by that limit
 * drops its oldest, possibly partial, pass and says so under the curve (`completePasses`). Ask Iris about the curve.
 */
function AcrissCurve({ sipp, data, view, askable }) {
	const [state, setState] = useState({ loading: true, items: [], truncated: false, passes: 0, error: null })
	const [tick, setTick] = useState(0)
	const [hover, setHover] = useState(null)
	const key = `${sipp}|${queryOf(view)}`
	useEffect(() => {
		const ctl = new AbortController()
		setState({ loading: true, items: [], truncated: false, passes: 0, error: null })
		setHover(null)
		fetchAcrissPushes(view, sipp, ctl.signal)
			.then(b => { if (!ctl.signal.aborted) setState({ loading: false, ...completePasses(b.items, b.total), error: null }) })
			.catch(e => { if (!ctl.signal.aborted) setState({ loading: false, items: [], truncated: false, passes: 0, error: e?.message || 'The passes could not be read.' }) })
		return () => ctl.abort()
	}, [key, tick])  // eslint-disable-line react-hooks/exhaustive-deps

	const lor = data?.sample?.lor || view.lor
	const curve = useMemo(() => buildPassCurve(state.items, { unit: view.unit, lor, tz: data?.window?.tz }), [state.items, view.unit, lor, data])
	const tip = hover ? passTooltip(curve, hover.d) : null

	return <Box component="section" aria-label={`${sipp} evolution`} sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', p: '12px 14px 10px', mb: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>Evolution of the average price</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>You vs your tier at each pass of the window · {unitWord(view.unit)} price, EUR</Typography>
			</Box>
			{askable && !state.loading && !state.error && !curve.empty && <AskIrisButton variant="label" tooltip="Ask Iris about this curve" getAsk={() => curveEvidence({ sipp, curve, data, view })}/>}
		</Box>
		{state.loading && <Skeleton variant="rounded" width="100%" height={CURVE_HEIGHT} aria-busy="true" sx={{ mt: 1, borderRadius: '10px', transform: 'none' }}/>}
		{!!state.error && <AppNote severity="error" sx={{ mt: 1 }}>
			<b>Could not load the curve.</b> The table below is up to date.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={() => setTick(t => t + 1)} sx={secondaryButtonSx}>Try again</Button></Box>
		</AppNote>}
		{!state.loading && !state.error && curve.empty && <Box sx={{ py: 2 }}><AppEmptyLine>No pass with a price for {sipp} in the window.</AppEmptyLine></Box>}
		{!state.loading && !state.error && !curve.empty && <>
			<Box sx={{ display: 'flex', gap: 1.5, mt: 1, fontSize: 12.5, color: dsColorNew.textDark }}>
				{curve.main.map(s => <Box key={s.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
					<Box component="i" sx={{ width: 14, height: 3, borderRadius: '2px', display: 'inline-block', backgroundColor: s.color }}/>{s.name}
				</Box>)}
			</Box>
			<Box sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
				<CurveChart xKind="pass" curve={curve} hidden={{}} showPrev={false} showOthers={false} code="EUR" hover={hover} onHover={setHover}
					label={passCurveLabel(curve, sipp)} height={CURVE_HEIGHT} allBullets/>
				{!!tip && <CurveTooltip tip={tip} x={hover.x} code="EUR"/>}
				<Box aria-live="polite" sx={visuallyHidden}>{tip ? tipText(tip, 'EUR') : ''}</Box>
			</Box>
			{state.truncated && <Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>{truncatedNote(state.passes)}</Typography>}
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
				Each point is the mean over the pick-up dates that pass decided for {sipp}; ○ hollow = fewer than 3 dates. Your price after a pass is the target shelf price when the decision counts (written, or simulated while Simulated is Include), the price it read otherwise.
			</Typography>
		</>}
	</Box>
}

/**
 * The detail of an ACRISS (criterion 19): its average of each rate's change vs the tier, its evolution curve by pass (phase 2,
 * `AcrissCurve`), and the per-date table from `by_acriss[].dates`; a date row opens the cell detail. Beside the Iris panel for Iris users.
 * The title names the code and its size class ("MDMR · Mini", `acrissClassName`): the app has no fuller catalogue.
 */
export default function AcrissDetailDialog({ open, sipp, data, view, todayIso, onClose, onOpenCell, askable = false }) {
	const row = (data?.by_acriss || []).find(r => r.sipp === sipp)
	if (!open || !row) return null
	const lor = data?.sample?.lor || view.lor
	const unit = view.unit
	const canAsk = askable && row.own?.change != null
	const withIris = canAsk && isIrisUser(userData.value)
	const whole = activityHref({ office: view.office, sipp, pickup_from: view.pickup_from, pickup_to: view.pickup_to, lor: view.lor })
	const className = acrissClassName(sipp)        // "MDMR · Mini" (criterion 19: code and name; the size class is the name the app has)

	return <AppDialog open={open} onClose={onClose} maxWidth="md" aria-labelledby="last-changes-acriss-title" {...besidePanel({}, { withIris, right: pushRight.value })}>
		<AppDialogHeader icon="car" title={<span id="last-changes-acriss-title"><b>{sipp}</b>{className ? ` · ${className}` : ''} — you vs your tier</span>}
			subtitle={`${data?.sample?.office_name || view.office} · ${lor} days · ${(data?.sample?.dates || []).length} pick-up dates · ${windowText(data?.window)} · ${unitWord(unit)} price, EUR`}
			actions={canAsk ? <AskIrisButton variant="label" tooltip={`Ask Iris about ${sipp}`} getAsk={() => acrissEvidence({ row, data, view })}/> : null} onClose={onClose}/>
		<AppDialogBody sx={{ ...dialogBodySx, maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mb: 2 }}>
				<Box sx={tile}><Typography sx={tileTitle}>You</Typography><Change ratio={row.own?.change} size={18}/><Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{money(inUnit(row.own?.start, unit, lor))} → {money(inUnit(row.own?.end, unit, lor))}</Typography></Box>
				<Box sx={tile}><Typography sx={tileTitle}>Your tier</Typography><Change ratio={row.market?.change} size={18}/><Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{money(inUnit(row.market?.start, unit, lor))} → {money(inUnit(row.market?.end, unit, lor))}</Typography></Box>
				<Box sx={tile}><Typography sx={tileTitle}>Δ vs tier</Typography><DeltaPill deltaPt={row.delta_pt} suffix=""/><Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>{deltaSentence(row.delta_pt, row.own?.change, row.market?.change)}</Typography></Box>
			</Box>
			<AcrissCurve sipp={sipp} data={data} view={view} askable={askable}/>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mb: 1 }}>By pick-up date</Typography>
			{!row.dates?.length && <AppEmptyLine>No date with a decision.</AppEmptyLine>}
			{!!row.dates?.length && <Table size="small" sx={{ '& td': { fontSize: 12.5, py: 0.75, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } }}>
				<TableHead><TableRow sx={{ '& th': { ...headCellSx, fontSize: 11 } }}>
					<TableCell>Pick-up</TableCell><TableCell align="right">Before</TableCell><TableCell align="right">Now</TableCell><TableCell align="right">You</TableCell>
					<TableCell align="right">Tier now</TableCell><TableCell align="right">Tier</TableCell><TableCell align="right">Δ</TableCell><TableCell>Status</TableCell>
				</TableRow></TableHead>
				<TableBody>
					{row.dates.map(d => <TableRow key={d.pickup_date} hover onClick={() => onOpenCell(sipp, d.pickup_date)} sx={{ cursor: 'pointer' }} title="Open the cell detail">
						<TableCell sx={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{pickupLabel(d.pickup_date, todayIso)}</TableCell>
						<TableCell align="right">{money(inUnit(d.own_start, unit, lor))}</TableCell>
						<TableCell align="right">{money(inUnit(d.own_end, unit, lor))}</TableCell>
						<TableCell align="right"><RatioCell ratio={d.own_change}/></TableCell>
						<TableCell align="right">{money(inUnit(d.market_end, unit, lor))}</TableCell>
						<TableCell align="right"><RatioCell ratio={d.market_change}/></TableCell>
						<TableCell align="right"><PointsCell pt={d.delta_pt}/></TableCell>
						<TableCell sx={{ color: dsColorNew.textMuted }}>{STATUS[d.status] || d.status}{d.n_passes ? ` · ${d.n_passes} pass${d.n_passes === 1 ? '' : 'es'}` : ''}
							{d.sim && <Chip size="small" label="SIM" title="Ends in a simulated decision: nothing was written to the PMS" sx={{ ...toneChipSx('accent'), ml: 0.75 }}/>}</TableCell>
					</TableRow>)}
				</TableBody>
			</Table>}
		</AppDialogBody>
		<AppDialogFooter>
			<Button variant="outlined" component="a" href={whole} target="_blank" rel="noopener" startIcon={<IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>Open in Activity</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}
